import type { FolderUser, FolderSearchResult } from '../../types';
import { useCallback, useRef, useState } from 'react';
import { TextField } from '@workramp/ui';
import useOutsideClick from '~/components/custom-hooks/use-outside-click';
import { AutocompleteResults, AutocompleteResultsList, Wrapper } from './AutocompleteInput.styles';

type Props = {
  onSelect: (result: FolderUser) => void;
  onSearch: (searchValue: string) => void;
  results: unknown[];
  renderResult: (result: FolderSearchResult, onClick: (result: FolderUser) => void) => JSX.Element;
};

export const AutocompleteInput = ({ onSelect, onSearch, results, renderResult }: Props) => {
  const [searchValue, setSearchValue] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const ref = useRef();

  const handleClose = useCallback(() => setShowAutocomplete(false), []);

  const handleUserClick = useCallback(
    (result: FolderUser) => {
      onSelect(result);
      handleClose();
      setSearchValue('');
    },
    [handleClose, onSelect],
  );

  useOutsideClick(ref, handleClose);

  return (
    <Wrapper ref={ref}>
      <TextField
        hideLabel
        label="Search by name or email"
        aria-label="Search by name or email"
        leadingIconName="search:regular"
        placeholder="Search by name"
        value={searchValue}
        onChange={(value) => {
          setSearchValue(value);
          setShowAutocomplete(true);
          onSearch(value);
        }}
        onFocus={() => {
          setShowAutocomplete(true);
        }}
      />
      {showAutocomplete && !!searchValue && !!results.length && (
        <AutocompleteResults>
          <AutocompleteResultsList>
            {results.map((result: FolderSearchResult) => (
              <li key={result.id}>{renderResult(result, handleUserClick)}</li>
            ))}
          </AutocompleteResultsList>
        </AutocompleteResults>
      )}
    </Wrapper>
  );
};
